import { FC, useEffect, useState } from 'react'
import { themeColors } from '@app/constants'
import { Box } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { CalendarTimeIcon, ClockIcon } from '@app/assets/icons'
import DateTimePicker from '@mui/lab/DateTimePicker'
import { CustomTextField } from '.'
import { DatePicker, TimePicker } from '@mui/lab'
import theme from '@app/lib/materialui/theme'
import { TranslateMessage } from '@app/lib/lang/TranslateMessage'

export interface CustomDateTimePickerProps {
	disabled?: boolean
	disablePast?: boolean
	maxDate?: Date
	minDate?: Date
	type?: 'date' | 'time' | 'dateTime'
	onChange: (date: Date | null, value?: string | null | undefined) => void
	value?: Date
	className?: any
	width?: number
	minutesStep?: number
	dataTestId?: string
}

export const CustomDateTimePicker: FC<CustomDateTimePickerProps> = ({
	type = 'dateTime',
	onChange: onChangeFromProps,
	value: valueFromProps,
	className,
	width,
	disabled,
	minutesStep = 1,
	dataTestId,
	...otherProps
}) => {
	const [value, setValue] = useState(valueFromProps)
	const [open, setOpen] = useState(false)
	const [isMinutesAlert, setIsMinutesAlert] = useState(false)
	const roundToMinutesStep = (date: Date | undefined): Date | undefined => {
		if (!date || minutesStep === 1) {
			setIsMinutesAlert(false)
			return date
		}

		const newDate = new Date(date)
		const minutes = newDate.getMinutes()
		const roundedMinutes = Math.round(minutes / minutesStep) * minutesStep

		if (minutes !== roundedMinutes) {
			setIsMinutesAlert(true)
		} else {
			setIsMinutesAlert(false)
		}

		newDate.setMinutes(roundedMinutes, 0, 0)

		return newDate
	}

	const onChange = (value: any) => {
		const roundedValue = roundToMinutesStep(value)
		setValue(roundedValue || undefined)
		onChangeFromProps(roundedValue || null)
	}

	useEffect(() => {
		if (!value) {
			const rounded = roundToMinutesStep(valueFromProps || undefined)
			setValue(rounded || undefined)
		}
	}, [valueFromProps])

	const getEndAdornment = () => {
		return (
			<InputAdornment position="end">
				<Box
					sx={{
						borderLeftColor: themeColors.borderColor,
						borderLeftStyle: 'solid',
						borderLeftWidth: '1px',
						paddingBottom: theme.spacing(1),
						paddingLeft: theme.spacing(2),
						paddingTop: theme.spacing(2),
						cursor: 'pointer',
					}}
					onClick={() => setOpen(true)}
				>
					{type === 'date' || type === 'dateTime' ? (
						<CalendarTimeIcon
							fill={themeColors.pinkishGrey}
							fontSize="1.2rem"
						/>
					) : (
						<ClockIcon
							fill={themeColors.pinkishGrey}
							fontSize="1.2rem"
						/>
					)}
				</Box>
			</InputAdornment>
		)
	}

	if (type === 'date') {
		return (
			<DatePicker
				InputProps={{
					// @ts-ignore
					'data-testid': dataTestId,
				}}
				mask="__.__.____"
				onChange={onChange}
				value={value || null}
				renderInput={(params: any) => (
					<CustomTextField
						{...params}
						endAdornment={getEndAdornment()}
					/>
				)}
				open={open}
				onClose={() => setOpen(false)}
				className={className}
				inputFormat="dd.MM.yyyy"
				disabled={disabled}
			/>
		)
	}

	if (type === 'time') {
		return (
			<TimePicker
				{...otherProps}
				InputProps={{
					// @ts-ignore
					'data-testid': dataTestId,
				}}
				ampm={false}
				mask="__:__"
				onChange={onChange}
				renderInput={(params: any) => (
					<CustomTextField
						{...params}
						endAdornment={getEndAdornment()}
					/>
				)}
				open={open}
				onClose={() => setOpen(false)}
				value={value || null}
				className={className}
				disabled={disabled}
				minutesStep={minutesStep}
			/>
		)
	}

	return (
		<>
			<DateTimePicker
				InputProps={{
					// @ts-ignore
					'data-testid': dataTestId,
				}}
				ampm={false}
				value={value || null}
				mask="__.__.____ __:__"
				renderInput={(params: any) => (
					<CustomTextField
						{...params}
						endAdornment={getEndAdornment()}
						disabled={disabled}
					/>
				)}
				open={open}
				onClose={() => setOpen(false)}
				className={className}
				onChange={onChange}
				inputFormat="dd.MM.yyyy HH:mm"
				disabled={disabled}
				minutesStep={minutesStep}
			/>
			{isMinutesAlert && (
				<Box sx={{ color: theme.palette.warning.main, marginTop: 1 }}>
					<TranslateMessage
						id="shared.validation.timeRounded"
						values={{ minutes: minutesStep }}
					/>
				</Box>
			)}
		</>
	)
}
